import { useCallback, useEffect, useState } from "react";

import fetcher_v2 from "@/api/fetcher_v2";
import * as Toast from "@radix-ui/react-toast";

import Message from "./Message";
import MessageContext, {
  MessageBody,
  MessageId,
  MessageInstance,
  PredefinedErrorMessage,
  PredefinedNeutralMessage,
  PredefinedSuccessMessage,
  PredefinedWarningMessage,
} from "./MessageContext";
import Messages from "./Messages";

type MessageProviderProps = {
  children?: React.ReactNode;
};

// Global id for keeping track of messages
let messageId = 0;

const createMessageInstance = (body: MessageBody): MessageInstance => {
  // Get a new id and increment the global id
  const id = messageId++;
  return { isVisible: true, _timeoutRef: undefined, id, ...body };
};

/**
 * Displays messages to the user. This is meant to replace the old Messaging service located in AE.Alert.alert.
 * It is also a thin wrapper around bootstrap to make it easier to replace in the future.
 *
 * In this first version:
 * - Messages are displayed in a fixed position at the top of the screen.
 * - Messages are displayed on top of eachother. Just like the old version, and the user can always close them manually.
 * - Messages are never cleared from the state, which should pose no problem for now, as the number of messages is expected to be low.
 */
const MessageProvider: React.FC<MessageProviderProps> = ({ children }) => {
  const [messages, setMessages] = useState<MessageInstance[]>([]);

  const removeMessage = useCallback((id: number) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) => {
        if (msg.id === id) {
          return { ...msg, isVisible: false };
        }
        // clear any existing timeouts
        if (msg?._timeoutRef) clearTimeout(msg._timeoutRef);
        return msg;
      })
    );
  }, []);

  /**
   * Clear the message with the given id from the state,
   * The idea here is that you can use it together with the
   * id that is returned from the message function to create
   * custom functionality.
   */
  const clearMessage = useCallback((id: MessageId) => {
    setMessages?.((prevMessages) =>
      prevMessages.map((msg) =>
        msg.id === id ? { ...msg, isVisible: false } : msg
      )
    );
  }, []);

  /**
   * Creates a message, displays it with some defaults and returns the id
   * of the created message.
   */

  const message = useCallback(
    async (body: MessageBody): Promise<MessageId> => {
      const newMessage = createMessageInstance(body);
      const { id, duration } = newMessage;

      setMessages((prevMessages) => [...prevMessages, newMessage]);

      // Set timeout to hide the message, if there is one.
      return new Promise((res) => {
        if (duration) {
          newMessage._timeoutRef = setTimeout(() => {
            clearMessage(id);
            res(id);
          }, duration);
        } else {
          res(id);
        }
      });
    },
    [clearMessage]
  );

  useEffect(() => {
    fetcher_v2<{
      messages: MessageBody[];
    }>("/a/api/v1/messages/").then(({ data }) => {
      setMessages([]);
      (data as { messages: MessageBody[] })?.messages?.map(
        (msg: MessageBody) => {
          message({ ...msg, duration: 5000 });
        }
      );
    });
  }, [message]);

  useEffect(() => {
    //Listen for the event
    const handleOutsideEvent = (evt: CustomEvent<MessageBody>) => {
      if (evt.detail) {
        message(evt.detail);
      }
    };

    window.addEventListener(
      "ae-message",
      (e) => handleOutsideEvent(e as CustomEvent<MessageBody>),
      false
    );

    return () =>
      // oxlint-disable-next-line unicorn/no-invalid-remove-event-listener
      window.removeEventListener("ae-message", (e) =>
        handleOutsideEvent(e as CustomEvent<MessageBody>)
      );
  }, [message]);

  /* Prefabricated helper functions to make code using this function more expressive
  - All of these have a preset timeout, except dangers which is considered fatal
  */

  const danger: PredefinedErrorMessage = useCallback(
    (props) => {
      return message({ type: "danger", ...props });
    },
    [message]
  );

  const info: PredefinedNeutralMessage = useCallback(
    (props) => {
      return message({ type: "info", duration: 5000, ...props });
    },
    [message]
  );

  const warning: PredefinedWarningMessage = useCallback(
    (props) => {
      return message({ type: "warning", duration: 5000, ...props });
    },
    [message]
  );

  const success: PredefinedSuccessMessage = useCallback(
    (props) => {
      return message({ type: "success", duration: 5000, ...props });
    },
    [message]
  );

  return (
    <MessageContext.Provider
      value={{ message, info, danger, warning, success, clearMessage }}
    >
      {children}
      <Toast.Provider>
        {messages.map((msg) => (
          <Message
            open={msg.isVisible}
            key={msg.id}
            {...msg}
            onOpenChange={(openState: boolean) => {
              if (openState === false) removeMessage(msg.id);
            }}
          />
        ))}
        <Messages />
      </Toast.Provider>
    </MessageContext.Provider>
  );
};

export default MessageProvider;
